import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCategories } from '../services/quizService';
import { Category, QuizSettings } from '../types/quiz';
import {
  Container,
  Card,
  Form,
  Label,
  Select,
  Input,
  Button,
  ErrorText,
  LoadingSpinner,
  Slider,
  SliderContainer,
  SliderValue
} from '../styles/StyledComponents';

interface QuizSettingsProps {
  onStartQuiz: (settings: QuizSettings) => void;
}

const QuizSettingsComponent: React.FC<QuizSettingsProps> = ({ onStartQuiz }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<QuizSettings>(() => {
    // Try to load settings from localStorage
    const savedSettings = localStorage.getItem('quizSettings');
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings);
      } catch (e) {
        console.error('Failed to parse saved settings:', e);
      }
    }
    // Default settings if nothing is saved
    return {
      amount: 10,
      category: 'any',
      difficulty: 'any',
      type: 'multiple',
      lives: 3,
      skips: 2,
      mode: 'endless'
    };
  });

  const navigate = useNavigate();

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        const fetchedCategories = await fetchCategories();
        setCategories(fetchedCategories);
        setError(null);
      } catch (err) {
        setError('Failed to load categories. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Convert numeric values
    const numericFields = ['amount', 'lives', 'skips'];
    const processedValue = numericFields.includes(name) ? parseInt(value, 10) : value;
    
    const updatedSettings = {
      ...settings,
      [name]: processedValue
    };
    
    // Update state
    setSettings(updatedSettings);
    
    // Save to localStorage
    localStorage.setItem('quizSettings', JSON.stringify(updatedSettings));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate settings
    if (settings.amount < 1 || settings.amount > 50) {
      setError('Number of questions must be between 1 and 50');
      return;
    }
    
    if (settings.lives < 1) {
      setError('Number of lives must be at least 1');
      return;
    }
    
    onStartQuiz(settings);
    navigate('/quiz');
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner />
        <p>Loading categories...</p>
      </Container>
    );
  }

  return (
    <Container>
      <Card>        
        <Form onSubmit={handleSubmit}>
          <Label htmlFor="mode">Game Mode:</Label>
          <Select
            id="mode"
            name="mode"
            value={settings.mode}
            onChange={handleChange}
          >
            <option value="standard">Standard Mode</option>
            <option value="endless">Endless Mode</option>
          </Select>

          {settings.mode === 'standard' && (
            <>
              <Label htmlFor="amount">Number of Questions:</Label>
              <Input
                type="number"
                id="amount"
                name="amount"
                min="1"
                max="50"
                value={settings.amount}
                onChange={handleChange}
              />
            </>
          )}

          <Label htmlFor="category">Category:</Label>
          <Select
            id="category"
            name="category"
            value={settings.category}
            onChange={handleChange}
          >
            <option value="any">Any Category</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>

          <Label htmlFor="difficulty">Difficulty:</Label>
          <Select
            id="difficulty"
            name="difficulty"
            value={settings.difficulty}
            onChange={handleChange}
          >
            <option value="any">Any Difficulty</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </Select>

          <Label htmlFor="type">Question Type:</Label>
          <Select
            id="type"
            name="type"
            value={settings.type}
            onChange={handleChange}
          >
            <option value="multiple">Multiple Choice</option>
            <option value="boolean">True / False</option>
          </Select>

          <Label htmlFor="lives">Number of Lives: {settings.lives}</Label>
          <SliderContainer>
            <SliderValue>
              
              
            </SliderValue>
            <Slider
              id="lives"
              name="lives"
              min="1"
              max="10"
              value={settings.lives}
              onChange={handleChange}
            />
          </SliderContainer>

          <Label htmlFor="skips">Number of Skips: {settings.skips}</Label>
          <SliderContainer>
            <SliderValue>              
            </SliderValue>
            <Slider
              id="skips"
              name="skips"
              min="0"
              max="5"
              value={settings.skips}
              onChange={handleChange}
            />
          </SliderContainer>

          {error && <ErrorText>{error}</ErrorText>}

          <Button type="submit">Start Quiz</Button>
        </Form>
      </Card>
    </Container>
  );
};

export default QuizSettingsComponent;
